import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, trackEvent } from "@/lib/apiClient";
import { Button } from "@/components/primitives";

function getUTM() {
  const p = new URLSearchParams(window.location.search);
  return {
    utm_source: p.get("utm_source") || "",
    utm_medium: p.get("utm_medium") || "",
    utm_campaign: p.get("utm_campaign") || "",
  };
}

export function LeadForm({ resourceSlug = "", campaign = "", source = "lead_magnet", testidPrefix = "lead" }) {
  const [form, setForm] = useState({ first_name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads", {
        ...form,
        lead_source: source,
        landing_page: window.location.pathname,
        resource_requested: resourceSlug,
        campaign,
        ...getUTM(),
        consent: true,
      });
      trackEvent("lead_magnet_download", { resource: resourceSlug });
      setDone(true);
      toast.success("Your guide is on its way.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (done)
    return (
      <div data-testid={`${testidPrefix}-success`} className="border border-[#BFDBFE] bg-[#F8F9FA] p-6 text-center">
        <p className="font-serif-display text-xl text-[#0F2C59]">Thank you.</p>
        <p className="text-slate-600 text-sm mt-2">Check your inbox — your guide is on its way.</p>
      </div>
    );

  return (
    <form onSubmit={submit} data-testid={`${testidPrefix}-form`} className="space-y-3">
      <input
        data-testid={`${testidPrefix}-first-name`}
        type="text"
        required
        placeholder="First name"
        value={form.first_name}
        onChange={(e) => setForm({ ...form, first_name: e.target.value })}
        className="w-full border border-slate-300 px-4 py-3 text-[#111827] focus:outline-none focus:border-[#0F2C59] focus:ring-1 focus:ring-[#0F2C59]"
      />
      <input
        data-testid={`${testidPrefix}-email`}
        type="email"
        required
        placeholder="Email address"
        value={form.email}
        onChange={(e) => setForm({ ...form, email: e.target.value })}
        className="w-full border border-slate-300 px-4 py-3 text-[#111827] focus:outline-none focus:border-[#0F2C59] focus:ring-1 focus:ring-[#0F2C59]"
      />
      <Button type="submit" data-testid={`${testidPrefix}-submit`} disabled={loading} className="w-full">
        {loading ? "Sending…" : "Send Me the Guide"}
      </Button>
      <p className="text-xs text-slate-500">No spam. Unsubscribe anytime.</p>
    </form>
  );
}

export function NewsletterForm({ testidPrefix = "newsletter", dark = false }) {
  const [form, setForm] = useState({ first_name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter", { ...form, source: "newsletter" });
      trackEvent("newsletter_signup", {});
      setDone(true);
      toast.success(data.message || "You're in.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = dark
    ? "w-full bg-white/10 border border-white/20 px-4 py-3 text-white placeholder-white/60 focus:outline-none focus:border-white"
    : "w-full border border-slate-300 px-4 py-3 focus:outline-none focus:border-[#0F2C59] focus:ring-1 focus:ring-[#0F2C59]";

  if (done)
    return (
      <p data-testid={`${testidPrefix}-success`} className={`font-serif-display text-lg ${dark ? "text-white" : "text-[#0F2C59]"}`}>
        You're in. Thanks for joining.
      </p>
    );

  return (
    <form onSubmit={submit} data-testid={`${testidPrefix}-form`} className="flex flex-col sm:flex-row gap-3 max-w-xl">
      <input
        data-testid={`${testidPrefix}-first-name`}
        type="text"
        placeholder="First name (optional)"
        value={form.first_name}
        onChange={(e) => setForm({ ...form, first_name: e.target.value })}
        className={inputCls + " sm:flex-1"}
      />
      <input
        data-testid={`${testidPrefix}-email`}
        type="email"
        required
        placeholder="Email address"
        value={form.email}
        onChange={(e) => setForm({ ...form, email: e.target.value })}
        className={inputCls + " sm:flex-1"}
      />
      <Button
        type="submit"
        data-testid={`${testidPrefix}-submit`}
        variant={dark ? "white" : "primary"}
        disabled={loading}
        className="whitespace-nowrap"
      >
        {loading ? "Joining…" : "Join the Newsletter"}
      </Button>
    </form>
  );
}
